import { useCallback, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { DateOfBirthAdultCheckFieldConfig } from "@evergiving/common/dist/es2015/form";

import { setFormValue, resetFormErrors } from "@/lib/redux/actions";
import { RootState } from "@/lib/redux/types";
import { LabelledField } from "@/lib/components/LabelledField";
import { LabelText } from "@/lib/components/LabelText";
import { UICheckbox } from "@/lib/components/UICheckbox";
import { FieldError } from "@/lib/components/FieldError";
import { expandYear, calendarToGregorian } from "@/lib/misc/dates";

type DateOfBirthAdultCheckProps = { config: DateOfBirthAdultCheckFieldConfig };

export const DateOfBirthAdultCheckField = ({
  config: { calendar, checkboxText, labelText, requiredFieldErrorText },
}: DateOfBirthAdultCheckProps) => {
  const name = "dateOfBirth";
  const required = !!requiredFieldErrorText;
  const [isChecked, setIsChecked] = useState(false);
  const anyErrors = useSelector((state: RootState) => state.errors[name]);
  const dispatch = useDispatch();
  const errorId = `${name}-errors`;
  const [isDirty, setIsDirty] = useState(false);

  const handleChange = useCallback(() => {
    const newCheckedStatus = !isChecked;

    setIsChecked(newCheckedStatus);

    if (newCheckedStatus) {
      dispatch(resetFormErrors(name));

      const newValue = calendarToGregorian(
        `${expandYear(
          new Date().getFullYear().toString(),
          "past",
          calendar
        )}-01-01`,
        calendar
      );

      dispatch(setFormValue(name, newValue, false));
    } else {
      dispatch(setFormValue(name, undefined, required));
    }
  }, [calendar, dispatch, isChecked, required]);

  return (
    <div>
      <LabelText requiredIndicator={required}>{labelText}</LabelText>
      <LabelledField id={name}>
        <UICheckbox
          isChecked={isChecked}
          onChange={handleChange}
          onFocus={() => setIsDirty(true)}
          aria-required={required}
          aria-invalid={(anyErrors && isDirty) || undefined}
          aria-describedby={anyErrors ? errorId : undefined}
        />
        <LabelText>{checkboxText}</LabelText>
      </LabelledField>
      <div role="alert">
        {anyErrors && isDirty ? (
          <FieldError id={errorId} messages={[requiredFieldErrorText]} />
        ) : null}
      </div>
    </div>
  );
};
